import * as Sentry from "@sentry/vue";
import { type QueryMeta, useQueryClient } from "@tanstack/vue-query";
import type { Plugin } from "vue";

import {
  ApiError,
  isNetworkError,
  isTimeoutError,
  RequestValidationApiError,
  SchemaValidationError,
  UnauthorizedApiError,
} from "@/api";
import { useToasts } from "@/composables/use-toasts.composable";
import { router } from "@/router";
import { useSessionStore } from "@/stores/session.store";

function isAbortError(error: unknown) {
  return error instanceof Error && error.name === "AbortError";
}

async function navigateToLogin() {
  try {
    await router.replace({ name: "login" });
  } catch (navigationError) {
    Sentry.captureException(navigationError);
  }
}

export const serverErrorPolicyPlugin: Plugin = {
  install(app) {
    const sessionStore = app.runWithContext(() => useSessionStore());
    const queryClient = app.runWithContext(() => useQueryClient());
    const toasts = useToasts();

    const handleError = (error: unknown, meta: QueryMeta | undefined) => {
      if (isAbortError(error)) {
        return;
      }

      if (error instanceof UnauthorizedApiError) {
        if (
          error.requestAuthentication === "session" &&
          sessionStore.session.status === "authenticated"
        ) {
          sessionStore.logout();
          toasts.showError("Login has expired. Please log in again.");
          void navigateToLogin();
        }

        return;
      }

      const showGlobalError = (message: string) => {
        if (meta?.notification !== "local") {
          toasts.showError(message);
        }
      };

      if (error instanceof RequestValidationApiError) {
        showGlobalError(
          "Could not process your request. Please try again later.",
        );
        return;
      }

      if (isNetworkError(error) || isTimeoutError(error)) {
        showGlobalError(
          "Could not connect to the server. Check your internet connection, or try again later.",
        );
        return;
      }

      if (error instanceof SchemaValidationError) {
        Sentry.captureException(error);
        showGlobalError(
          "An unexpected error occurred. Please try again later.",
        );
        return;
      }

      if (error instanceof ApiError) {
        if (error.userMessage) {
          showGlobalError(error.userMessage);
          return;
        }

        Sentry.captureException(error);
        showGlobalError(
          "An unexpected error occurred. Please try again later.",
        );
        return;
      }

      Sentry.captureException(error);
      showGlobalError("An unexpected error occurred. Please try again later.");
    };

    const unsubscribeFromQueries = queryClient
      .getQueryCache()
      .subscribe((event) => {
        if (event.type === "updated" && event.action.type === "error") {
          handleError(event.action.error, event.query.meta);
        }
      });
    const unsubscribeFromMutations = queryClient
      .getMutationCache()
      .subscribe((event) => {
        if (event.type === "updated" && event.action.type === "error") {
          handleError(event.action.error, event.mutation.meta);
        }
      });

    app.onUnmount(() => {
      unsubscribeFromQueries();
      unsubscribeFromMutations();
    });
  },
};
